import React, { forwardRef, useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import { useTabs } from './context'

const FADE_PX = 40

const TabList = forwardRef((props, ref) => {
    const { className, children, style, ...rest } = props

    const { variant } = useTabs()

    const scrollRef = useRef(null)
    // Indicadores de scroll: si hay contenido oculto a izquierda/derecha
    // aplicamos un fade (mask) en ese borde para dar a entender (sobre todo
    // en mobile) que hay mas tabs para scrollear. Se auto-ocultan cuando
    // todas las tabs caben o cuando se llega al extremo del scroll.
    // Usamos mask-image (no un overlay de color) para no depender del color
    // de fondo del contenedor y no agregar un wrapper que rompa el layout.
    const [overflow, setOverflow] = useState({ left: false, right: false })

    // Mergeamos el ref interno (para medir el scroll) con el ref forwarded
    // que algunos consumidores pasan al tablist.
    const setRefs = useCallback((node) => {
        scrollRef.current = node
        if (typeof ref === 'function') {
            ref(node)
        } else if (ref) {
            ref.current = node
        }
    }, [ref])

    const updateOverflow = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const maxScroll = el.scrollWidth - el.clientWidth
        const nextLeft = el.scrollLeft > 1
        const nextRight = el.scrollLeft < maxScroll - 1
        setOverflow((prev) =>
            prev.left === nextLeft && prev.right === nextRight
                ? prev
                : { left: nextLeft, right: nextRight }
        )
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        updateOverflow()
        el.addEventListener('scroll', updateOverflow, { passive: true })
        // ResizeObserver cubre el cambio de ancho del contenedor (resize de
        // ventana, rotacion). El cambio de tabs se cubre en el efecto de abajo.
        const ro = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updateOverflow)
            : null
        ro?.observe(el)
        window.addEventListener('resize', updateOverflow)
        return () => {
            el.removeEventListener('scroll', updateOverflow)
            ro?.disconnect()
            window.removeEventListener('resize', updateOverflow)
        }
    }, [updateOverflow])

    // Recalcular cuando cambian las tabs (ej: una tab que se carga async y no
    // altera el ancho del contenedor, por lo que el ResizeObserver no dispara).
    // Es barato: solo mide el DOM y no re-renderiza si el overflow no cambio.
    useEffect(() => {
        updateOverflow()
    }, [children, updateOverflow])

    const tabListClass = classNames(
        'tab-list',
        `tab-list-${variant}`,
        className
    )

    const fadeStyle = {
        '--tab-fade-left': overflow.left ? `${FADE_PX}px` : '0px',
        '--tab-fade-right': overflow.right ? `${FADE_PX}px` : '0px',
    }

    return (
        <div
            role="tablist"
            className={tabListClass}
            ref={setRefs}
            style={{ ...fadeStyle, ...style }}
            {...rest}
        >
            {children}
        </div>
    )
})

export default TabList
